import React, { useState } from 'react';
import { Sparkles, ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiGetJson as apiGet } from '../../../../utils/api';

// Full-width JPRO Brief hero for the dashboard landing. Self-hides when there
// is no brief for the day (or it has no items), so it never shows an empty card.
const OverviewBrief = () => {
  const [showSources, setShowSources] = useState(false);
  const { data } = useQuery({
    queryKey: ['news', 'brief'],
    queryFn: () => apiGet('/news/brief'),
    staleTime: 1000 * 60 * 30,
  });

  const brief = data?.brief || null;
  if (!brief || !brief.items?.length) return null;

  const dateLabel = brief.brief_date
    ? new Date(`${brief.brief_date}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    : '';

  // Footnote numbering across the shown bullets (first-appearance order), so the
  // superscripts and the Sources list use the same contiguous numbers.
  const shown = brief.items.slice(0, 3);
  const order = [];
  shown.forEach((it) => (it.refs || []).forEach((r) => {
    if (!order.some((o) => o.id === r.id)) order.push(r);
  }));
  const numberFor = new Map(order.map((r, i) => [r.id, i + 1]));

  return (
    <section className="relative overflow-hidden rounded-2xl border border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900/40">
      {/* hairline accent */}
      <span className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-blue-600 via-blue-500 to-orange-400" />

      <div className="p-5">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-blue-600 dark:text-blue-400" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700/80 dark:text-blue-300/80">
            JPRO Brief
          </span>
          {dateLabel && (
            <>
              <span className="text-gray-300 dark:text-gray-700">·</span>
              <span className="text-[11px] text-gray-400 dark:text-gray-500">{dateLabel}</span>
            </>
          )}
        </div>

        {brief.headline && (
          <h2 className="mt-2.5 font-dm-serif text-[18px] lg:text-[19px] leading-snug text-gray-900 dark:text-white">
            {brief.headline}
          </h2>
        )}

        <ul className="mt-3 space-y-2">
          {shown.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-[7px] shrink-0 h-1.5 w-1.5 rounded-full bg-blue-500" />
              <span className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300 font-rasa">
                {item.text}
                {(item.refs || []).map((r) => (
                  <a
                    key={r.id}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`${r.title}${r.source ? ` — ${r.source}` : ''}`}
                    className="align-super ml-0.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {numberFor.get(r.id)}
                  </a>
                ))}
              </span>
            </li>
          ))}
        </ul>

        {order.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => setShowSources((v) => !v)}
              aria-expanded={showSources}
              className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <ChevronDown size={12} className={`transition-transform ${showSources ? '' : '-rotate-90'}`} />
              Sources ({order.length})
            </button>
            {showSources && (
            <ol className="mt-1.5 space-y-0.5">
              {order.map((r) => (
                <li key={r.id} className="text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
                  <span className="tabular-nums">{numberFor.get(r.id)}.</span>{' '}
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                    {r.title}
                  </a>
                  {r.source && <span> · {r.source}</span>}
                </li>
              ))}
            </ol>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default OverviewBrief;
